import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Update } from '../types';
import updatesService from '../services/updatesService';
import { categoryLabelFor, categoryStyleFor } from '../utils/updatePresentation';

const DISMISSED_KEY = 'notizy.dismissedFeaturedUpdate';

/**
 * Uitgelicht updatebericht op de frontpage. Donkergroen merkvlak dat opvalt
 * tegen de lichte pagina; klikt door naar de detailpagina. Sluitbaar — de keuze
 * wordt per update-id onthouden, zodat een nieuw bericht weer verschijnt.
 */
const FeaturedUpdateCard: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [update, setUpdate] = useState<Update | null>(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let active = true;
        updatesService.getFeaturedUpdate().then(featured => {
            if (!active) return;
            setUpdate(featured);
            if (featured) {
                try {
                    setDismissed(localStorage.getItem(DISMISSED_KEY) === featured.id);
                } catch {
                    // localStorage niet beschikbaar — toon de kaart gewoon
                }
            }
        });
        return () => {
            active = false;
        };
    }, [i18n.language]);

    if (!update || dismissed) {
        return null;
    }

    const open = () => navigate(`/updates/${update.slug}`);

    const handleDismiss = (event: React.MouseEvent) => {
        event.stopPropagation();
        setDismissed(true);
        try {
            localStorage.setItem(DISMISSED_KEY, update.id);
        } catch {
            // niets persisteren als localStorage ontbreekt
        }
    };

    const category = categoryStyleFor(update.category);

    return (
        <div
            onClick={open}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    open();
                }
            }}
            className="group relative mb-5 cursor-pointer overflow-hidden rounded-xl border border-primary-100 bg-container shadow-soft transition-all duration-200 hover:border-[color:var(--hover-outline)] hover:shadow-medium animate-fade-in"
        >
            {/* Sluiten */}
            <button
                onClick={handleDismiss}
                className="absolute right-2.5 top-2.5 z-10 rounded-full p-1 transition-colors hover:bg-primary-600/[0.06]"
                style={{ color: 'var(--text-muted)' }}
                title={t('featuredUpdateCard.dismiss')}
                aria-label={t('featuredUpdateCard.dismiss')}
            >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            <div className="flex items-center gap-3.5 px-4 py-3.5 pr-9">
                {/* Sparkle-icoontegel */}
                <div
                    className="flex h-9 w-9 flex-none items-center justify-center rounded-lg"
                    style={{ background: 'var(--accent-tint)' }}
                    aria-hidden="true"
                >
                    <svg className="h-[18px] w-[18px] text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
                    </svg>
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                            style={{ background: category.bg, color: category.color }}
                        >
                            {categoryLabelFor(update)}
                        </span>
                    </div>
                    <h2 className="mt-1 truncate font-serif text-base leading-snug" style={{ color: 'var(--text-strong)' }}>
                        {update.title}
                    </h2>
                    <p className="truncate text-sm" style={{ color: 'var(--text-muted)' }}>
                        {update.summary}
                    </p>
                </div>

                <div className="hidden flex-none items-center gap-1 text-sm font-semibold text-primary-700 sm:flex">
                    <span>{t('featuredUpdateCard.view')}</span>
                    <svg
                        className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default FeaturedUpdateCard;
